"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Building2, Hammer, Factory, HardHat, Construction, Flame } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  to: string;
  badge: string;
  iconName: string;
}
const services: ServiceItem[] = [{
  id: "temp-roofs",
  title: "Temporary Roofs",
  description: "Weatherproof containment systems protecting ongoing structural works from rain, wind, and site exposure.",
  to: "/TemporaryRoofsPage",
  badge: "Weather Protection",
  iconName: "ShieldCheck"
}, {
  id: "new-build",
  title: "New Build Scaffolding",
  description: "Progressive, staged external access frameworks built for residential developments and commercial builds.",
  to: "/NewBuildScaffoldingPage",
  badge: "Construction",
  iconName: "Building2"
}, {
  id: "refurbishment",
  title: "Refurbishment Scaffolding",
  description: "Adaptive access scaffolds tailored for facade upgrades, heritage restoration, and structural remodeling.",
  to: "/RefurbishmentScaffoldingPage",
  badge: "Renovation",
  iconName: "Hammer"
}, {
  id: "industrial",
  title: "Industrial Scaffolding",
  description: "Heavy-duty load-bearing scaffolding engineered for plants, factories, and utility facilities.",
  to: "/IndustrialScaffoldingPage",
  badge: "Heavy Duty",
  iconName: "Factory"
}, {
  id: "specialist-access",
  title: "Specialist Access Scaffolding",
  description: "Custom-designed cantilever, suspended, and bridging scaffolds for complex structural obstacles.",
  to: "/SpecialistAccessScaffoldingPage",
  badge: "Engineered",
  iconName: "HardHat"
}, {
  id: "edge-protection",
  title: "Edge Protection",
  description: "Robust handrails, toe boards, and safety barriers ensuring site personnel safety at height.",
  to: "/EdgeProtectionPage",
  badge: "Fall Prevention",
  iconName: "Construction"
}, {
  id: "chimney-scaffolding",
  title: "Chimney Scaffolding",
  description: "Secure, non-invasive roof saddles and perimeter platforms for chimney repairs and re-pointing.",
  to: "/ChimneyScaffoldingPage",
  badge: "Roof Level",
  iconName: "Flame"
}];
const renderIcon = (name: string) => {
  switch (name) {
    case "ShieldCheck":
      return <ShieldCheck className="size-5 text-primary" />;
    case "Building2":
      return <Building2 className="size-5 text-primary" />;
    case "Hammer":
      return <Hammer className="size-5 text-primary" />;
    case "Factory":
      return <Factory className="size-5 text-primary" />;
    case "HardHat":
      return <HardHat className="size-5 text-primary" />;
    case "Construction":
      return <Construction className="size-5 text-primary" />;
    case "Flame":
      return <Flame className="size-5 text-primary" />;
    default:
      return <HardHat className="size-5 text-primary" />;
  }
};
export default function ServicesGrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mb-12 sm:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Core Capabilities
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground">
            Scaffolding solutions tailored to your project
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Engineered access and safety systems delivered across London, Berkshire, Surrey, Hampshire, Buckinghamshire, and the South East.
          </p>
        </div>

        {/* 7-Card Responsive Grid: 1 col on mobile, 2 on tablet, 3 on desktop */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => <motion.div key={service.id} data-index={index} variants={cardVariants} className={`flex flex-col h-full ${index === 6 ? "md:col-span-2 lg:col-span-1" : ""}`}>
              <Card className="h-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] hover:border-primary/50 group">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="size-10 rounded-lg bg-accent flex items-center justify-center border border-border">
                        {renderIcon(service.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs font-normal">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default tracking-tight text-card-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 border-t border-border/50 flex items-center justify-between">
                  <Button asChild variant="ghost" size="sm" className="p-0 h-auto font-medium text-primary hover:bg-transparent hover:text-primary/80 group/btn">
                    <Link to={service.to} className="inline-flex items-center gap-1.5 text-sm">
                      <span>View service</span>
                      <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Supporting CTA Strip */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 bg-accent/40 rounded-xl p-6 sm:p-8">
          <div>
            <h3 className="text-lg font-semibold font-default text-foreground">
              Need custom access design or temporary work calculations?
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Our safety-led team provides site surveys and full compliance documentation.
            </p>
          </div>
          <Button asChild size="default" className="shrink-0 font-medium">
            <Link to="/ContactRequestQuotePage">Request a quote</Link>
          </Button>
        </div>
      </div>
    </section>;
}